import { useParams, Navigate, Link } from "react-router-dom";
import { Reveal, SectionHead, PrimaryButton, PageHero, Marquee, DotList } from "@/components/bits";
import { ServiceCard, CTABanner } from "@/pages/Home";
import { SERVICES, MARQUEE_ITEMS } from "@/lib/data";
import { Check, ArrowLeft } from "lucide-react";

const WRAP = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";

export function ServicesPage() {
  return (
    <main>
      <PageHero
        eyebrow="Services"
        title="Every shipping type, one accountable broker."
        lead="Whether it is LTL, FTL, container shipments or intermodal drayage, we specialize in handling all shipping types with reliable, fast transportation services."
      />
      <section className="py-16 lg:py-24">
        <div className={WRAP}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="services-grid">
            {SERVICES.map((s, i) => <ServiceCard key={s.slug} s={s} i={i} />)}
          </div>
        </div>
      </section>
      <div className="mb-20 lg:mb-28"><Marquee items={MARQUEE_ITEMS} /></div>
      <CTABanner />
    </main>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) return <Navigate to="/services" replace />;
  const others = SERVICES.filter((x) => x.slug !== slug).slice(0, 4);

  return (
    <main>
      <section className="hero-reference relative overflow-hidden text-white" data-testid="service-detail-hero">
        <div className={`${WRAP} relative pt-10 pb-16 lg:pt-14 lg:pb-24`}>
          <Reveal>
            <Link to="/services" data-testid="service-detail-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:underline underline-offset-4 transition-colors">
              <ArrowLeft className="h-4 w-4" /> All services
            </Link>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal delay={0.05}><p className="label text-brand-light">Service</p></Reveal>
              <Reveal delay={0.1}>
                <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]" data-testid="service-detail-title">{s.name}</h1>
              </Reveal>
              <Reveal delay={0.18}>
                <p className="mt-5 text-lg text-slate-200 leading-relaxed max-w-xl">{s.tagline}</p>
              </Reveal>
              <Reveal delay={0.26}>
                <div className="mt-8">
                  <PrimaryButton to="/quote" testid="service-detail-quote-cta">Get a quote for this lane</PrimaryButton>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.2}>
                <div className="rounded-3xl border border-white/30 bg-white/10 p-2 shadow-soft">
                  <div className="overflow-hidden rounded-2xl">
                    <img src={s.image} alt={s.name} className="img-zoom w-full aspect-[4/3] object-cover" data-testid="service-detail-image" />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-8`}>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="card p-6 sm:p-10">
                <h2 className="font-display text-2xl font-bold text-ink">Overview</h2>
                {s.overview.map((p, i) => (
                  <p key={i} className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed">{p}</p>
                ))}
                <h3 className="mt-10 font-display text-xl font-bold text-ink">What's included on every load</h3>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="service-detail-included">
                  {s.included.map((inc) => (
                    <li key={inc} className="flex items-start gap-2.5 text-[15px] text-ink font-medium rounded-2xl bg-paper px-4 py-3">
                      <Check className="h-5 w-5 text-corpblue shrink-0" /> {inc}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="panel-reference p-8 lg:p-10" data-testid="service-detail-panel">
                <p className="label text-brand-light">Capabilities</p>
                <div className="mt-6">
                  <DotList light items={s.bullets} />
                </div>
                <div className="mt-9 border-t border-white/10 pt-7">
                  <p className="font-display text-xl font-bold text-white">Ready to move {s.name.toLowerCase()}?</p>
                  <p className="mt-2 text-slate-300 text-sm leading-relaxed">Rates back within the hour during dispatch hours.</p>
                  <div className="mt-5">
                    <PrimaryButton to="/quote" testid="service-detail-panel-cta">Request a Quote</PrimaryButton>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className={WRAP}>
          <SectionHead eyebrow="Explore more" title="Other ways we move freight." />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {others.map((o, i) => <ServiceCard key={o.slug} s={o} i={i} />)}
          </div>
        </div>
      </section>
    </main>
  );
}
